
import { useState, useCallback, useRef, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiCall } from '@/lib/api/api-helpers'

// Search result types
export interface ProjectSearchResult {
  id: string
  name: string
  type: 'project' | 'canvas'
  lastModified: string
  canvasCount?: number
  thumbnail: string | null
  category: string
  slug: string
  project?: {
    name: string
    uuid: string
  } | null
}

export type ProjectSearchFilter = 'all' | 'recent' | 'drafts' | 'canvas' | 'files'

interface ProjectSearchResponse {
  success: boolean
  data: ProjectSearchResult[]
  error?: string
  meta: {
    total: number
    query?: string
    filter?: string
  }
}

interface UseProjectSearchReturn {
  searchResults: ProjectSearchResult[]
  isSearching: boolean
  error: string | null
  searchProjects: (query: string, filter?: ProjectSearchFilter) => Promise<void>
  clearResults: () => void
}

export function useProjectSearch(): UseProjectSearchReturn {
  const [searchResults, setSearchResults] = useState<ProjectSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated } = useAuth()
  const abortRef = useRef<AbortController | null>(null)

  // Abort in-flight request on unmount
  useEffect(() => {
    return () => { abortRef.current?.abort() }
  }, [])

  // Search projects
  const searchProjects = useCallback(async (query: string, filter: ProjectSearchFilter = 'all') => {
    if (!isAuthenticated) {
      setSearchResults([])
      return
    }

    try {
      setIsSearching(true)
      setError(null)

      // Abort any in-flight request
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      // Build query string
      const params = new URLSearchParams()
      if (query.trim()) {
        params.append('query', query.trim())
      }
      if (filter !== 'all') {
        params.append('filter', filter)
      }
      params.append('limit', '20')

      const queryString = params.toString() ? `?${params.toString()}` : ''
      const response = await apiCall(`/api/projects/search${queryString}`, isAuthenticated, { signal: controller.signal }) as ProjectSearchResponse

      if (response.success && response.data) {
        setSearchResults(response.data)
      } else {
        throw new Error(response.error || 'Failed to search projects')
      }
    } catch (err) {
      // Ignore aborted requests — a newer search has superseded this one
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('Failed to search projects:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to search projects'

      // Don't show error for connection refused (API not running)
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('CONNECTION_REFUSED')) {
        setError('API server not running')
      } else {
        setError(errorMessage)
      }
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [isAuthenticated])

  // Clear results
  const clearResults = useCallback(() => {
    setSearchResults([])
    setError(null)
  }, [])

  return {
    searchResults,
    isSearching,
    error,
    searchProjects,
    clearResults,
  }
}